/**
 * CityFitsQuiz — the "Which City Fits Me?" 60-second homepage widget.
 *
 * Six questions, one at a time, deterministic scoring over 338 cities.
 * Backend endpoint: POST /api/city-quiz/match.
 *
 * We deliberately keep this all-CSS transitions and no third-party
 * animation lib — the sparkle comes from the sequencing itself.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ArrowRight, RotateCcw, MapPin, Trophy } from "lucide-react";

const FLAG = {
  UK: "🇬🇧", US: "🇺🇸", DE: "🇩🇪", FR: "🇫🇷", NL: "🇳🇱", IE: "🇮🇪",
  IT: "🇮🇹", ES: "🇪🇸", CH: "🇨🇭", AU: "🇦🇺", CA: "🇨🇦", SG: "🇸🇬",
  AE: "🇦🇪", IN: "🇮🇳", KR: "🇰🇷", FI: "🇫🇮", SE: "🇸🇪", DK: "🇩🇰",
  NO: "🇳🇴", JP: "🇯🇵", HK: "🇭🇰", AT: "🇦🇹", BE: "🇧🇪", CZ: "🇨🇿",
  PT: "🇵🇹", NZ: "🇳🇿", MY: "🇲🇾", TH: "🇹🇭", TW: "🇹🇼", ZA: "🇿🇦",
};

export default function CityFitsQuiz() {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);        // 0..N-1, N = results
  const [answers, setAnswers] = useState({}); // {question_id: value}
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    api.get("/city-quiz/config")
      .then((r) => setConfig(r.data))
      .catch(() => setErr("Could not load quiz."));
  }, []);

  const questions = config?.questions || [];
  const total = questions.length;
  const current = questions[step];
  const done = started && step >= total;
  const progress = started ? Math.min(100, ((step) / total) * 100) : 0;

  async function submit(finalAnswers) {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.post("/city-quiz/match", finalAnswers);
      setResults(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  function pick(value) {
    if (!current) return;
    const next = { ...answers, [current.id]: value };
    setAnswers(next);
    if (step + 1 >= total) {
      setStep(step + 1);
      submit(next);
    } else {
      setStep(step + 1);
    }
  }

  function reset() {
    setStep(0); setAnswers({}); setResults(null); setStarted(false);
  }

  // ------------- INTRO CARD -----------------------------------------
  if (!started) {
    return (
      <section
        data-testid="city-quiz-widget"
        className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-16"
      >
        <div className="relative overflow-hidden rounded-2xl border border-stone-200 bg-gradient-to-br from-[#FDF6EC] via-[#FFFCF6] to-[#F5EEDF] shadow-sm">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#92400E]/8 blur-3xl" />
          <div className="absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-[#002147]/6 blur-3xl" />
          <div className="relative px-8 py-14 lg:px-14 lg:py-16 flex flex-col lg:flex-row lg:items-center gap-10">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#92400E]">
                <Sparkles className="h-3.5 w-3.5" /> New · 60-second quiz
              </div>
              <h2 className="mt-3 font-editorial text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#002147]">
                Which city
                <span className="italic text-[#92400E]"> fits me?</span>
              </h2>
              <p className="mt-4 text-stone-700 max-w-xl leading-relaxed">
                Rankings can't tell you if you'll feel at home in a place.
                Six questions, one minute, and we'll match you against 338 real
                student cities using climate, cost, size, English fluency and
                the vibe you're after.
              </p>
              <button
                onClick={() => setStarted(true)}
                data-testid="city-quiz-start"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#002147] hover:bg-[#001A38] text-white px-6 py-3 text-sm font-medium shadow-sm transition"
              >
                Start the quiz <ArrowRight className="h-4 w-4" />
              </button>
              <div className="mt-4 text-[11px] text-stone-500 uppercase tracking-wider font-mono">
                No sign-up · No email · Deterministic scoring across 338 cities
              </div>
            </div>
            <div className="lg:w-72 shrink-0">
              <div className="rounded-xl border border-stone-200 bg-white p-5 text-sm">
                <div className="font-editorial italic text-stone-700 leading-snug">
                  "Turned out my perfect fit was <b>Utrecht</b> — never had it on my list."
                </div>
                <div className="mt-3 text-[11px] uppercase tracking-wider text-stone-500 font-mono">
                  Sophie · Y13 · Manchester
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ------------- RESULTS --------------------------------------------
  if (done) {
    return (
      <section
        data-testid="city-quiz-results"
        className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-16"
      >
        <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-stone-100 px-8 py-5 bg-gradient-to-r from-[#FDF6EC] to-white flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#92400E] flex items-center gap-1.5">
                <Trophy className="h-3.5 w-3.5" /> Your matches
              </div>
              <div className="font-editorial text-2xl mt-1 text-[#002147]">
                {loading ? "Scoring 338 cities…" : "Top 5 cities that fit you"}
              </div>
            </div>
            <button
              onClick={reset}
              data-testid="city-quiz-retake"
              className="text-xs inline-flex items-center gap-1.5 text-stone-500 hover:text-[#002147]"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Retake
            </button>
          </div>

          {err && (
            <div className="p-6 text-red-700 bg-red-50 text-sm" data-testid="city-quiz-error">{err}</div>
          )}

          {loading && !results && (
            <div className="p-8 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-stone-100 animate-pulse rounded-lg" />
              ))}
            </div>
          )}

          {results && (
            <div className="divide-y divide-stone-100">
              {results.matches.map((m, i) => (
                <Link
                  key={m.slug}
                  to={`/cities/${m.slug}`}
                  data-testid={`city-quiz-match-${m.slug}`}
                  className="p-5 lg:p-6 flex items-start gap-5 hover:bg-stone-50 transition group"
                >
                  <div className="shrink-0 h-14 w-14 rounded-xl bg-[#FDF6EC] flex flex-col items-center justify-center">
                    <div className="text-2xl leading-none">{FLAG[m.country_code] || "🌍"}</div>
                    <div className="font-mono text-[10px] mt-0.5 text-[#92400E] font-semibold">
                      #{i + 1}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h3 className="font-editorial text-xl text-[#002147]">{m.name}</h3>
                      <span className="text-xs text-stone-500 uppercase tracking-wider">· {m.country}</span>
                    </div>
                    <div className="mt-1.5 text-sm text-stone-600">{m.reason}</div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex-1 max-w-xs h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#92400E] to-[#7C3AED]"
                          style={{ width: `${Math.round(m.score * 100)}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-stone-500 font-underground-sc tracking-wider">
                        {Math.round(m.score * 100)}% fit
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-stone-400 group-hover:text-[#002147] group-hover:translate-x-0.5 transition-transform mt-3" />
                </Link>
              ))}
            </div>
          )}

          <div className="border-t border-stone-100 bg-stone-50 px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] text-stone-500 font-underground-sc tracking-wider flex items-center gap-2 flex-wrap">
              <MapPin className="h-3 w-3" />
              Scored {results?.total_scored ?? "…"} cities · weights climate 15% · size 15% · budget 20% · English 15% · vibe 15% · priority 20%
            </div>
            <Link
              to="/cities/matcher"
              data-testid="city-quiz-open-matcher"
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#002147] text-white hover:bg-[#0a3160] shrink-0"
            >
              Refine with full filters <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // ------------- QUESTION CARD --------------------------------------
  return (
    <section
      data-testid="city-quiz-question"
      className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-16"
    >
      <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-stone-100">
          <div
            className="h-full bg-gradient-to-r from-[#92400E] to-[#7C3AED] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="px-8 py-10 lg:px-14 lg:py-14">
          <div className="flex items-center justify-between mb-6">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#92400E]">
              Question {step + 1} / {total}
            </div>
            <button
              onClick={reset}
              className="text-[11px] text-stone-400 hover:text-stone-700 uppercase tracking-wider"
              data-testid="city-quiz-cancel"
            >
              Cancel
            </button>
          </div>

          {current && (
            <>
              <h3 className="font-editorial text-3xl sm:text-4xl leading-tight text-[#002147]">
                {current.label}
              </h3>

              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {current.options.map((opt) => (
                  <button
                    key={String(opt.value)}
                    onClick={() => pick(opt.value)}
                    data-testid={`city-quiz-option-${current.id}-${opt.value}`}
                    className="text-left rounded-xl border-2 border-stone-200 hover:border-[#92400E] hover:bg-[#FDF6EC]/40 transition p-5 group"
                  >
                    <div className="font-editorial text-lg text-[#002147] group-hover:text-[#92400E]">
                      {opt.label}
                    </div>
                    {opt.hint && (
                      <div className="mt-1 text-xs text-stone-500 leading-snug">{opt.hint}</div>
                    )}
                  </button>
                ))}
              </div>

              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  data-testid="city-quiz-back"
                  className="mt-8 text-xs text-stone-500 hover:text-[#002147] uppercase tracking-wider"
                >
                  ← Back
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
